import { NgIf } from '@angular/common';
import { Component, Input, OnDestroy, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { FuseAlertComponent } from '@fuse/components/alert';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import { FuseNavigationItem, FuseNavigationService, FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { TranslocoModule } from '@ngneat/transloco';
import { BlockchainWalletLowBalanceThreshold } from 'app/app.const';
import { AuthService } from 'app/core/auth/auth.service';
import { ApiBlockchainSettingService } from 'app/core/apis/api-blockchain-setting.service';
import { ApiCompanyPointWalletService } from 'app/core/apis/api-company-point-wallet.service';
import { UserDataService } from 'app/core/datas/user-data';
import { Company, Project } from 'app/core/models/base.model';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { UserComponent } from 'app/layout/common/user/user.component';
import { LanguagesComponent } from 'app/layout/common/languages/languages.component';
import { environment } from 'environments/environment';
import { catchError, forkJoin, of, Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'compact-company-layout',
    templateUrl: './compact-company.component.html',
    encapsulation: ViewEncapsulation.None,
    imports: [FuseAlertComponent, FuseLoadingBarComponent, MatButtonModule, MatIconModule, UserComponent, LanguagesComponent, NgIf, RouterOutlet, FuseVerticalNavigationComponent, TranslocoModule]
})
export class CompactCompanyLayoutComponent implements OnInit, OnDestroy {
    private _userDataSvc = inject(UserDataService);
    private _authSvc = inject(AuthService);
    private _apiBlockchainSettingSvc = inject(ApiBlockchainSettingService);
    private _apiWalletSvc = inject(ApiCompanyPointWalletService);
    isScreenSmall: boolean;
    navigation: Navigation;

    private _unsubscribeAll: Subject<any> = new Subject<any>();
    appVersion: string = environment.appVersion;
    debug: boolean = !environment.production;
    @Input() projectid: number;
    @Input() project: Project;

    @Input() companyid: number;
    @Input() company: Company;

    // Shown when blockchain stamping is enabled for this company but its blockchain wallet balance is running low
    showLowBalanceAlert: boolean = false;
    lowBalanceAlertDismissed: boolean = false;
    walletBalance: number = 0;
    walletLowBalanceThreshold: number = BlockchainWalletLowBalanceThreshold;

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _navigationService: NavigationService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
    ) {

    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) => {
                // Use company navigation items directly
                this.navigation = navigation;
            });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');
            });

        this._checkBlockchainWalletBalance();
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }
    }

    clickBack() {
        this._router.navigate(['select', 'projects']);
    }

    onLogoClick(): void {
        this._navigationService.hijackLogoNavigation();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Loads this company's blockchain setting and blockchain wallet, and shows a low-balance
     * alert when blockchain stamping is enabled but the wallet balance has run low.
     */
    private _checkBlockchainWalletBalance(): void {
        if (!this.companyid) {
            return;
        }

        forkJoin({
            setting: this._apiBlockchainSettingSvc.apiGetByCompany({ company_id: this.companyid }).pipe(catchError(() => of(null))),
            wallet: this._apiWalletSvc.apiGetByCompany({ company_id: this.companyid, type: 'blockchain' }).pipe(catchError(() => of(null))),
        })
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ setting, wallet }) => {
                this.walletBalance = wallet ? (parseFloat(wallet.totalRemain) || 0) : 0;
                const effectivelyEnabled = !!setting?.enabled && (setting?.rules?.length ?? 0) > 0;
                this.showLowBalanceAlert = effectivelyEnabled && this.walletBalance < this.walletLowBalanceThreshold;
            });
    }
}
